import React, { useEffect, useState } from "react";
import { Form, Input, message } from "antd";
import CustomModal from "../../../components/Common/CustomModal";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "utils/validators";
import { createUser } from "services/superAdmin/userService";
import { UserCreatePayload } from "types/superAdmin/userTypes";

type DirectorFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  // password?: string; // optional if you invite via email; keep commented if backend sends setup email
};

interface AddDirectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  instanceId: string;              // 🔒 required: we attach director to this instance
  onCreated?: (payload: UserCreatePayload) => Promise<boolean>; // Update this line
}

const AddDirectorModal: React.FC<AddDirectorModalProps> = ({
  isOpen,
  onClose,
  instanceId,
  onCreated,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<DirectorFormValues>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    form.resetFields();
  }, [isOpen, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const v = await form.validateFields();

      const payload: UserCreatePayload = {
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        phoneNumber: v.phoneNumber || undefined,
        instanceId,               
        role: "director",         
        status: "active",
      };

      // await createUser(payload);
    //   message.success(t("user_created_successfully") || "Director created successfully");
      const ok = await onCreated?.(payload);
      if (ok) {
        onClose();
        form.resetFields();
      }
    } catch (err) {
      console.error(err);
      message.error(t("validation.please_fill_all_required_fields"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal
      title={t("add_director")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={loading}
      saveText={t("add")}
    >
      <Form form={form} layout="vertical">
        <div className="row">
          <div className="col-md-4">
            <Form.Item
              name="firstName"
              label={t("first_name")}
              rules={[{ required: true, message: t("validation.firstName_required") }]}
            >
              <Input placeholder={t("first_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              name="lastName"
              label={t("last_name")}
              rules={[{ required: true, message: t("validation.lastName_required") }]}
            >
              <Input placeholder={t("last_name")} />
            </Form.Item>
          </div>

          <div className="col-md-4">
            <Form.Item
              name="email"
              label={t("email")}
              rules={[
                { required: true, message: t("validation.please_enter_email") },
                { type: "email", message: t("validation.please_enter_valid_email") || "Invalid email" },
              ]}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>
          </div>

          <div className="col-md-6">
            <Form.Item
              name="phoneNumber"
              label={t("phone_number")}
              rules={[
                {
                  validator: (_, value) =>
                    value && !isValidPhoneNumber(value)
                      ? Promise.reject(t("validation.phone_number_invalid"))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input placeholder={t("phone_number")} />
            </Form.Item>
          </div>
        </div>
      </Form>
    </CustomModal>
  );
};

export default AddDirectorModal;
